import { useStore } from '@nanostores/react';
import { useQueryClient } from '@tanstack/react-query';
import { useModals } from '@customerio/pluma-components/react';
import { FlexPlanSheetModal } from '@/features/flex-plan-sheet';
import { billing } from '@/services/billing';
import { AddFundsModal } from '../components/add-funds-modal';
import { AiCreditsPurchaseModal } from '../components/ai-credits-purchase-modal';
import { BuilderCreditsPurchaseModal } from '../components/builder-credits-purchase-modal';
import type { AddFundsFunding } from '../utils/add-funds';
import type { CreditsPurchasePageName } from '../utils/credits-purchase-page-name';
import { invalidateCreditBalances } from '../utils/credit-balance-queries';
import { useCreditPurchaseGating } from './use-credit-purchase-gating';

// After a save-card purchase, the backend sets the default payment method asynchronously, so
// /billing/info may not reflect it immediately. Re-initialize a few times so the saved card appears
// without a manual reload.
const REFRESH_ATTEMPTS = 3;
const REFRESH_DELAY_MS = 2000;

type PurchaseResult = { savedCard: boolean };

/** Purchase CTA props threaded into a credit display: the click handler and disabled state/message. */
export type CreditDisplayPurchaseProps = {
  onPurchase?: () => void;
  isPurchaseDisabled: boolean;
  purchaseDisabledMessage: string;
};

export type CreditPurchaseActions = {
  aiCredits: CreditDisplayPurchaseProps;
  builderCredits: CreditDisplayPurchaseProps;
  /**
   * Opens the AI-credits modal and resolves once it has closed. For a surface that holds the modal
   * in the URL and has to know when to take it out again.
   */
  openAiCredits: () => Promise<void>;
  /**
   * Opens the Builder top-up modal on a set number of dollars, for a preset such as the wallet
   * card's Add $50. Callers that offer no preset use `builderCredits.onPurchase`, which opens on the
   * config amount.
   *
   * Carries no disabled state of its own: a preset must honour `builderCredits.isPurchaseDisabled`,
   * which is what refuses a top-up the account has no headroom for.
   *
   * Resolves once the modal has closed, for the same reason {@link openAiCredits} does.
   */
  openBuilderTopUp: (amountDollars?: number) => Promise<void>;
  openAddFunds: (options: AddFundsOptions) => Promise<void>;
};

export type AddFundsOptions = {
  hasFunded: boolean;
  amountDollars?: number;
  funding?: AddFundsFunding;
};

export type CreditPurchaseOptions = {
  /** The surface these CTAs sit on, reported with every event the purchase forms fire. */
  pageName: CreditsPurchasePageName;
};

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

/**
 * Wires the AI-credits and Builder-credits purchase flows: opens the managed purchase modals and
 * refreshes the balances after a successful purchase. Returns the purchase props to spread onto the
 * credit displays.
 *
 * Every page that shows a balance owns its own row and CTA, and calls this for the flow behind it.
 * Safe to mount more than once. The `?purchaseModal=` link is deliberately not handled here — see
 * {@link usePurchaseModalDeepLink}, which has one owner.
 */
export function useCreditPurchase(options: CreditPurchaseOptions): CreditPurchaseActions {
  const modals = useModals();
  const queryClient = useQueryClient();
  const gating = useCreditPurchaseGating();
  const isOrdwayCustomer = useStore(billing.isOrdwayCustomer);

  const refreshAfterPurchase = async ({ savedCard }: PurchaseResult) => {
    await invalidateCreditBalances(queryClient);
    if (!savedCard) {
      return;
    }
    for (let attempt = 0; attempt < REFRESH_ATTEMPTS; attempt++) {
      await delay(REFRESH_DELAY_MS);
      await billing.initialize.perform({ refresh: true });
    }
  };

  const openAiCreditsModal = async () => {
    const modalId = modals.open(
      AiCreditsPurchaseModal,
      { useOrdway: isOrdwayCustomer, pageName: options.pageName },
      { shouldCloseOnUnmount: true },
    );
    const result = await modals.waitForClose(modalId);
    if (result.reason === 'confirm') {
      await refreshAfterPurchase(result.data ?? { savedCard: false });
    }
  };

  const openBuilderCreditsModal = async (initialAmountDollars?: number) => {
    const modalId = modals.open(
      BuilderCreditsPurchaseModal,
      {
        currentBalanceCents: gating.builderCreditsBalanceCents,
        pageName: options.pageName,
        initialAmountDollars,
      },
      { shouldCloseOnUnmount: true },
    );
    const result = await modals.waitForClose(modalId);
    if (result.reason === 'confirm') {
      await refreshAfterPurchase(result.data ?? { savedCard: false });
    }
  };

  const openAddFundsModal = async ({ hasFunded, amountDollars, funding }: AddFundsOptions) => {
    const modalId = modals.open(
      AddFundsModal,
      {
        currentBalanceCents: gating.builderCreditsBalanceCents,
        hasFunded,
        pageName: options.pageName,
        initialAmountDollars: amountDollars,
        funding,
      },
      { shouldCloseOnUnmount: true },
    );
    const result = await modals.waitForClose(modalId);
    if (result.reason === 'confirm') {
      await refreshAfterPurchase(result.data ?? { savedCard: false });
      return;
    }
    // The sheet replaces the modal, and its Add funds brings the modal back on the same funding.
    if (result.data?.viewFlexPricing === true) {
      modals.open(
        FlexPlanSheetModal,
        {
          addFunds: {
            onPurchase: () => void openAddFundsModal({ hasFunded, amountDollars, funding }),
            isPurchaseDisabled: gating.isPurchaseBuilderCreditsDisabled,
            purchaseDisabledMessage: gating.purchaseBuilderCreditsDisabledMessage,
          },
        },
        { shouldCloseOnUnmount: true },
      );
    }
  };

  // The CTAs always render; the gating drives their disabled state and tooltip (trial, plan status,
  // billing-edit block, and — for Builder — the balance cap), so the button is never hidden outright.
  return {
    aiCredits: {
      onPurchase: () => void openAiCreditsModal(),
      isPurchaseDisabled: gating.isPurchaseCreditsDisabled,
      purchaseDisabledMessage: gating.purchaseCreditsDisabledMessage,
    },
    builderCredits: {
      onPurchase: () => void openBuilderCreditsModal(),
      isPurchaseDisabled: gating.isPurchaseBuilderCreditsDisabled,
      purchaseDisabledMessage: gating.purchaseBuilderCreditsDisabledMessage,
    },
    openAiCredits: () => openAiCreditsModal(),
    openBuilderTopUp: (amountDollars?: number) => openBuilderCreditsModal(amountDollars),
    openAddFunds: (addFundsOptions: AddFundsOptions) => openAddFundsModal(addFundsOptions),
  };
}
